import classnames from "classnames";
import { observer } from 'mobx-react';
import moment from "moment";
import React, { useCallback, useEffect, useMemo } from 'react';
import { COLORS, ICONS, ROUTES } from 'shared/constants';
import { CalendarItemTypeEnum } from "shared/enums";
import { useNavigate, useStore } from "shared/hooks";
import { UserModel } from "shared/models";
import { usersBirthdaysQuery } from "shared/queries";

import { UiAvatar, UiButton, UiCard, UiCardFooter, UiCardTitle, UiDate, UiIcon, UiNavLink } from 'shared/uikit';

import './styles.scss';

const UserItem = observer(({ user }: { user: UserModel }) => {
    return (
        <UiNavLink to={ROUTES.USER(user.slug)} className='w-users-birthdays-item'>
            <div className="w-users-birthdays-item__avatar">
                <UiAvatar size={40} label={user.previewName} image={user.imageAvatar}/>
            </div>
            <div className="w-users-birthdays-item__inner">
                <div className="w-users-birthdays-item__name">{user.previewName}</div>
                <div className="w-users-birthdays-item__position">{user.userPositionName}</div>
                <UiDate value={user.birthdayAt} format={'D MMMM'}/>
            </div>
        </UiNavLink>
    )
});

export const WUsersBirthdays = observer(() => {
    const navigate = useNavigate();
    const store = useStore(() => ({
        items: [] as UserModel[],
        date: moment(),
        tab: 'week',
        isOpened: false,
        isLoading: true,
        isSuccess: false,
    }));

    const fetchItems = useCallback(async () => {
        store.set('isLoading', true);
        store.set('isSuccess', false);
        const response = await usersBirthdaysQuery();
        if (response.isSuccess && response.data) {
            store.set('items', response.data.items.map(item => new UserModel(item)));
            store.set('isSuccess', true);
        }
        store.set('isLoading', false);
    }, [store]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const usersSorted = useMemo(() => {
        return store.items.slice().sort((a, b) => {
            return +a.birthDayAtMoment.format('MDD') - +b.birthDayAtMoment.format('MDD');
        });
    }, [store.items]);

    const usersMonth = useMemo(() => {
        const year = +moment().year();
        const month = +moment().format('M');
        const startOfDay = moment().startOf('day');

        return usersSorted
            .filter(user => {
                if (store.isOpened) {
                    return +user.birthDayAtMoment.format('M') === month;
                }
                const date = user.birthDayAtMoment.clone().set('year', year);
                return date.isSameOrAfter(startOfDay) && +user.birthDayAtMoment.format('M') === month;
            });
    }, [usersSorted, store.isOpened]);

    const usersWeek = useMemo(() => {
        const startOfDay = moment().startOf('day');
        const startOfWeek = moment().startOf('week');
        const end = +moment().endOf('week');
        const year = +moment().year();

        return usersSorted
            .filter(user => {
                const date = user.birthDayAtMoment.clone().set('year', year);
                if (store.isOpened) {
                    return date.isSameOrAfter(startOfWeek) && date.isSameOrBefore(end)
                }
                return date.isSameOrAfter(startOfDay) && date.isSameOrBefore(end)
            });
    }, [usersSorted, store.isOpened]);

    const usersSliced = (store.tab === 'week' ? usersWeek : usersMonth).slice(0, store.isOpened ? 1000 : 4);

    return (
        <UiCard isLoading={store.isLoading} className='w-users-birthdays'>
            <UiCardTitle label='Дни рождения' collapsable={'w-users-birthdays'}/>
            <div className="w-users-birthdays-tabs">
                <div className={classnames('w-users-birthdays-tabs__item', {
                    'w-users-birthdays-tabs__item--active': store.tab === 'week'
                })} onClick={() => store.set('tab', 'week')}>
                    <i/><span>На этой неделе</span>
                </div>
                <div className={classnames('w-users-birthdays-tabs__item', {
                    'w-users-birthdays-tabs__item--active': store.tab === 'month'
                })} onClick={() => store.set('tab', 'month')}>
                    <i/><span>В этом месяце</span>
                </div>
            </div>
            <div className="w-users-birthdays__section">
                <div className='w-users-birthdays__items'>
                    {usersSliced.map(user => <UserItem key={`month.${user.id}`} user={user}/>)}
                </div>
                <UiButton
                    isLink
                    isSmall
                    label={store.isOpened ? 'Свернуть' : 'Развернуть'}
                    iconAfter={
                        <UiIcon
                            color={COLORS.BROWN_1}
                            size={14}
                            icon={store.isOpened ? ICONS.CHEVRON_UP : ICONS.CHEVRON_DOWN}
                        />
                    }
                    onClick={() => store.set("isOpened", !store.isOpened)}
                />
            </div>
        </UiCard>
    );
});
